import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Subscription, forkJoin, merge } from 'rxjs';
import { APP_ROUTES } from '../../../core/constants/app-routes';
import { FLASH_MESSAGES } from '../../../core/constants/flash-messages';
import {
  dureeBailDefaut,
  parseDureeBailDto,
} from '../../../core/models/locataire-bail.model';
import { Locataire } from '../../../core/models/locataire.model';
import {
  isUniteFermee,
  Logement,
  toDateIso,
} from '../../../core/models/logement.model';
import { FlashService } from '../../../core/services/flash.service';
import { LocataireService } from '../../../core/services/locataire.service';
import { LogementService } from '../../../core/services/logement.service';
import { I18nValuePipe } from '../../../core/pipes/i18n-value.pipe';
import {
  applyDureeBailDefaut,
  createLocataireForm,
  dureeBailFormErrorKey,
  dureeBailFormHintKey,
  isDureeBailFormInvalid,
  locataireFromFormValue,
  logementOptionLabel,
  resolveLogementMeuble,
} from '../locataire-form';
import { LocataireFormFieldsComponent } from '../locataire-form-fields/locataire-form-fields.component';

@Component({
  selector: 'app-fiche-locataire',
  templateUrl: './fiche-locataire.component.html',
  styleUrls: ['./fiche-locataire.component.css'],
  imports: [
    RouterLink,
    FaIconComponent,
    TranslateModule,
    DatePipe,
    I18nValuePipe,
    ReactiveFormsModule,
    LocataireFormFieldsComponent,
  ],
})
export class FicheLocataireComponent implements OnInit, OnDestroy {
  @ViewChild('deleteDialog')
  private readonly deleteDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild('reactivateDialog')
  private readonly reactivateDialog?: ElementRef<HTMLDialogElement>;

  locataire: Locataire | null = null;
  logements: Logement[] = [];
  loading = true;
  error = false;
  editMode = false;
  saving = false;
  formError = false;
  confirmingDelete = false;
  deleting = false;
  deleteError = false;
  pendingDefinitiveDelete = false;
  confirmingReactivate = false;
  reactivating = false;
  reactivateError = false;
  selectedMeuble = false;
  readonly routes = APP_ROUTES;

  readonly form = createLocataireForm(this.fb, () => this.selectedMeuble);

  private locataireId?: string;
  private formSubs?: Subscription;
  private skipDureeReset = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly locataireService: LocataireService,
    private readonly logementService: LogementService,
    private readonly flashService: FlashService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = true;
      this.loading = false;
      return;
    }
    this.locataireId = id;

    forkJoin({
      locataire: this.locataireService.findById(id),
      logements: this.logementService.getLogement(),
    }).subscribe({
      next: ({ locataire, logements }) => {
        this.locataire = locataire;
        this.logements = this.buildLogementOptions(logements ?? [], locataire);
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });

    this.formSubs = merge(
      this.form.controls.logementId.valueChanges,
      this.form.controls.etudiant.valueChanges
    ).subscribe(() => {
      if (this.skipDureeReset) {
        return;
      }
      this.selectedMeuble = applyDureeBailDefaut(
        this.form,
        this.logements,
        this.locataire?.logementDto
      );
    });
  }

  ngOnDestroy(): void {
    this.formSubs?.unsubscribe();
  }

  get dureeBailHintKey(): string {
    return dureeBailFormHintKey(this.form, this.selectedMeuble);
  }

  get dureeBailErrorKey(): string {
    return dureeBailFormErrorKey(this.form, this.selectedMeuble);
  }

  get dureeBailInvalid(): boolean {
    return isDureeBailFormInvalid(this.form);
  }

  fullName(): string {
    if (!this.locataire) {
      return '';
    }
    return (
      `${this.locataire.prenomDto ?? ''} ${this.locataire.nomDto ?? ''}`.trim() ||
      this.translate.instant('locataires.sansNom')
    );
  }

  logementLabel(): string {
    const logement = this.locataire?.logementDto;
    if (!logement) {
      return this.translate.instant('locataires.logementInconnu');
    }
    return logementOptionLabel(logement, (key) => this.translate.instant(key));
  }

  canSoftDelete(): boolean {
    return !!this.locataire && this.locataire.activeDto !== false;
  }

  canDeleteDefinitive(): boolean {
    return !!this.locataire?.suppressionDefinitivePossibleDto;
  }

  canReactivate(): boolean {
    return !!this.locataire && this.locataire.activeDto === false;
  }

  startEdit(): void {
    if (!this.locataire || this.saving || this.deleting || this.reactivating) {
      return;
    }
    this.editMode = true;
    this.formError = false;
    this.patchFormFromLocataire(this.locataire);
  }

  cancelEdit(): void {
    if (this.saving) {
      return;
    }
    this.editMode = false;
    this.formError = false;
  }

  askDelete(): void {
    if (
      !this.locataire ||
      (!this.canSoftDelete() && !this.canDeleteDefinitive()) ||
      this.saving ||
      this.deleting ||
      this.reactivating
    ) {
      return;
    }
    this.deleteError = false;
    this.pendingDefinitiveDelete = false;
    this.confirmingDelete = true;
    queueMicrotask(() => this.deleteDialog?.nativeElement.showModal());
  }

  cancelDelete(): void {
    if (this.deleting) {
      return;
    }
    this.deleteDialog?.nativeElement.close();
    this.confirmingDelete = false;
    this.pendingDefinitiveDelete = false;
  }

  onDeleteDialogCancel(event: Event): void {
    if (this.deleting) {
      event.preventDefault();
      return;
    }
    this.confirmingDelete = false;
    this.pendingDefinitiveDelete = false;
  }

  onDeleteDialogBackdropClick(event: MouseEvent): void {
    this.closeDeleteDialogFromBackdrop(event);
  }

  onDeleteDialogBackdropKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.closeDeleteDialogFromBackdrop(event);
  }

  confirmDelete(definitive: boolean): void {
    if (!this.locataireId || this.deleting || !this.confirmingDelete) {
      return;
    }
    if (definitive && !this.canDeleteDefinitive()) {
      return;
    }
    if (!definitive && !this.canSoftDelete()) {
      return;
    }
    this.deleting = true;
    this.pendingDefinitiveDelete = definitive;
    this.deleteError = false;
    const request = definitive
      ? this.locataireService.deleteDefinitive(this.locataireId)
      : this.locataireService.delete(this.locataireId);
    request.subscribe({
      next: () => {
        this.deleting = false;
        this.deleteDialog?.nativeElement.close();
        this.confirmingDelete = false;
        this.pendingDefinitiveDelete = false;
        this.flashService.success(
          definitive
            ? FLASH_MESSAGES.locataireSupprimeDefinitivement
            : FLASH_MESSAGES.locataireSupprime
        );
        void this.router.navigate([APP_ROUTES.locataires]);
      },
      error: () => {
        this.deleting = false;
        this.deleteError = true;
      },
    });
  }

  private closeDeleteDialogFromBackdrop(event: Event): void {
    if (event.target !== event.currentTarget || this.deleting) {
      return;
    }
    this.cancelDelete();
  }

  askReactivate(): void {
    if (
      !this.locataire ||
      !this.canReactivate() ||
      this.saving ||
      this.deleting ||
      this.reactivating
    ) {
      return;
    }
    this.reactivateError = false;
    this.confirmingReactivate = true;
    queueMicrotask(() => this.reactivateDialog?.nativeElement.showModal());
  }

  cancelReactivate(): void {
    if (this.reactivating) {
      return;
    }
    this.reactivateDialog?.nativeElement.close();
    this.confirmingReactivate = false;
  }

  onReactivateDialogCancel(event: Event): void {
    if (this.reactivating) {
      event.preventDefault();
      return;
    }
    this.confirmingReactivate = false;
  }

  onReactivateDialogBackdropClick(event: MouseEvent): void {
    this.closeReactivateDialogFromBackdrop(event);
  }

  onReactivateDialogBackdropKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.closeReactivateDialogFromBackdrop(event);
  }

  confirmReactivate(): void {
    if (!this.locataireId || this.reactivating || !this.confirmingReactivate) {
      return;
    }
    this.reactivating = true;
    this.reactivateError = false;
    this.locataireService.reactiver(this.locataireId).subscribe({
      next: (saved) => {
        this.locataire = saved;
        this.logements = this.buildLogementOptions(this.logements, saved);
        this.reactivating = false;
        this.reactivateDialog?.nativeElement.close();
        this.confirmingReactivate = false;
        this.flashService.success(FLASH_MESSAGES.locataireReactive);
        if (saved?.densiteAlerteDto) {
          this.flashService.success(FLASH_MESSAGES.locataireDensiteAlerte, 5000);
        }
      },
      error: () => {
        this.reactivating = false;
        this.reactivateError = true;
      },
    });
  }

  private closeReactivateDialogFromBackdrop(event: Event): void {
    if (event.target !== event.currentTarget || this.reactivating) {
      return;
    }
    this.cancelReactivate();
  }

  save(): void {
    if (!this.locataire || !this.locataireId || this.saving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = locataireFromFormValue(this.form.getRawValue(), {
      idLocataireDto: this.locataireId,
      dateSortieDto: this.locataire.dateSortieDto,
      activeDto: this.locataire.activeDto !== false,
      idContactDto: this.locataire.idContactDto,
    });

    this.saving = true;
    this.formError = false;

    this.locataireService.update(payload).subscribe({
      next: (saved) => {
        this.locataire = saved;
        this.logements = this.buildLogementOptions(this.logements, saved);
        this.saving = false;
        this.editMode = false;
        if (saved?.densiteAlerteDto) {
          this.flashService.success(FLASH_MESSAGES.locataireDensiteAlerte, 5000);
        }
      },
      error: () => {
        this.saving = false;
        this.formError = true;
      },
    });
  }

  private patchFormFromLocataire(locataire: Locataire): void {
    const logementId = locataire.logementDto?.idLogementDto ?? '';
    this.selectedMeuble = resolveLogementMeuble(
      this.logements.find((l) => l.idLogementDto === logementId) ??
        locataire.logementDto
    );

    const parsed = parseDureeBailDto(locataire.dureeBailDto);
    const defaut = dureeBailDefaut({
      meuble: this.selectedMeuble,
      etudiant: !!locataire.etudiantDto,
    });

    this.skipDureeReset = true;
    this.form.reset({
      civilite: locataire.civiliteDto ?? '',
      nom: locataire.nomDto ?? '',
      prenom: locataire.prenomDto ?? '',
      telephone: locataire.telephoneDto ?? '',
      mail: locataire.mailDto ?? '',
      dateNaissance: toDateIso(locataire.dateNaissanceDto),
      villeNaissance: locataire.villeNaissanceDto ?? '',
      logementId,
      dateEntree: toDateIso(locataire.dateEntreeDto),
      dateBail: toDateIso(locataire.dateBailDto),
      dateCles: toDateIso(locataire.dateClesDto),
      etudiant: !!locataire.etudiantDto,
      dureeValeur: parsed?.valeur ?? defaut.valeur,
      dureeUnite: parsed?.unite ?? defaut.unite,
    });
    this.skipDureeReset = false;
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private buildLogementOptions(
    source: Logement[],
    locataire: Locataire | null
  ): Logement[] {
    const currentId = locataire?.logementDto?.idLogementDto;
    const byId = new Map<string, Logement>();
    for (const logement of source) {
      if (!logement.idLogementDto) {
        continue;
      }
      if (!isUniteFermee(logement) || logement.idLogementDto === currentId) {
        byId.set(logement.idLogementDto, logement);
      }
    }
    if (currentId && locataire?.logementDto && !byId.has(currentId)) {
      byId.set(currentId, locataire.logementDto);
    }
    const translate = (key: string) => this.translate.instant(key);
    return [...byId.values()].sort((a, b) =>
      logementOptionLabel(a, translate).localeCompare(
        logementOptionLabel(b, translate),
        'fr'
      )
    );
  }
}
